import FrameBuffer from './FrameBuffer';
import { dummyBatchShader, dummyShader, BatchShader, Shader, ShaderProgram } from './Shader';
import { Texture } from './Texture';
import { WebGLView, canvasOptions, contextOptions } from './WebGLView';

// const MAX_VERTEX_INDEX = 65536; // WebGL specs

// Limiting number of vertices that can be rendered in batch in order to achieve
// a good performance compromise across all devices
export const VERTEX_INDEX_LIMIT = 65536; // Cannot be more than MAX_VERTEX_INDEX

// Batch limitation unit is in bytes
// Therefore, the maximum number of quads can be deduced
// from the number of bytes a quad uses (which differs from each type of quad)
// At least 4 bytes per vertex
// The greater the number of bytes per vertex, the smaller the number of quads that can be batched
// i.e if VERTEX_INDEX_LIMIT is 16384, then MAX_BYTES_PER_BATCH is: 16384 * 4 = 65536
//     therefore, if each vertex takes 4 bytes,  the number of quads that can be batched is: 65536 / 4  / 4 = 16384 / 4 = 4096
//           but, if each vertex takes 16 bytes, the number of quads that can be batched is: 65536 / 16 / 4 = 4096  / 4 = 1024
export const MAX_BYTES_PER_BATCH = VERTEX_INDEX_LIMIT * 4;

let rendererUID = 0;

/// #if debug
let drawCount = 0;
/// #endif

export class Renderer extends WebGLView {
	activeShader: Shader;
	private _activeBlending: any;

	private _batchShader: BatchShader;
	private _batchTextures: Array<Texture>;
	private _batchBlending: any;

	private _frameBufferStack: Array<FrameBuffer>;
	private _batchSize: number;
	// TODO: rename to projectionMatrix
	private _vpMatrixStack: Array<Array<number>>;
	vertexBufferBinder: WebGLBuffer;
	quadIndexBuffer: WebGLBuffer;
	// cuboidIndexBuffer: WebGLBuffer;

	id: number;
	supportedExtensions: Array<string> | null;
	canvasFrameBuffer: FrameBuffer;
	activeFrameBuffer: FrameBuffer | null;
	_canvasFrameBufferBinder: WebGLFramebuffer;

	floatView: Float32Array;
	uLongView: Uint32Array;
	longView: Int32Array;
	uShortView: Uint16Array;
	shortView: Int16Array;
	byteView: Uint8Array;
	uByteView: Int8Array;

	constructor (canvasOptions: canvasOptions = {}, contextOptions: contextOptions = {}) {
		super(canvasOptions, contextOptions);

		const gl = this.gl;

		this.id = rendererUID++;
		this.supportedExtensions = gl.getSupportedExtensions();

		this.activeShader = dummyShader;
		this._activeBlending = null;

		this._batchShader = dummyBatchShader;
		this._batchTextures = [];
		this._batchBlending = null;
		this._batchSize = 0;

		this.activeFrameBuffer = null;

		this.canvasFrameBuffer = new FrameBuffer(0, 0, this.width, this.height);
		this._canvasFrameBufferBinder = gl.getParameter(gl.FRAMEBUFFER_BINDING);
		this.canvasFrameBuffer.binders[this.id] = this._canvasFrameBufferBinder;
		this._frameBufferStack = [this.canvasFrameBuffer];

		// Creation of the vertex buffers
		const arrayBuffer = new ArrayBuffer(MAX_BYTES_PER_BATCH);

		// Creation of views on the vertex buffer
		this.floatView  = new Float32Array(arrayBuffer);
		this.uLongView  = new Uint32Array(arrayBuffer);
		this.longView   = new Int32Array(arrayBuffer);
		this.uShortView = new Uint16Array(arrayBuffer);
		this.shortView  = new Int16Array(arrayBuffer);
		this.byteView   = new Uint8Array(arrayBuffer);
		this.uByteView  = new Int8Array(arrayBuffer);

		const vertexBufferBinder = gl.createBuffer();
		if (vertexBufferBinder === null) {
			throw new Error('Could not create vertex buffer');
		}

		gl.bindBuffer(gl.ARRAY_BUFFER, vertexBufferBinder);
		gl.bufferData(gl.ARRAY_BUFFER, arrayBuffer, gl.DYNAMIC_DRAW);
		this.vertexBufferBinder = vertexBufferBinder;

		// note: index buffer only works for quads
		const quadIndexBufferBinder = gl.createBuffer();
		if (quadIndexBufferBinder === null) {
			throw new Error('Could not create quad index buffer');
		}
		const quadIndexBuffer = new Uint16Array(VERTEX_INDEX_LIMIT * 6 / 4);

		for (let j = 0, i = 0; j < VERTEX_INDEX_LIMIT; j += 4, i += 6) {
			quadIndexBuffer[i]     = j;
			quadIndexBuffer[i + 1] = j + 1;
			quadIndexBuffer[i + 2] = j + 2;
			quadIndexBuffer[i + 3] = j + 3;
			quadIndexBuffer[i + 4] = j + 2;
			quadIndexBuffer[i + 5] = j + 1;
		}

		// uploading index data
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, quadIndexBufferBinder);
		gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, quadIndexBuffer, gl.STATIC_DRAW);
		this.quadIndexBuffer = quadIndexBufferBinder;

		// // note: index buffer only works for quads
		// const cuboidIndexBufferBinder = gl.createBuffer();
		// if (cuboidIndexBufferBinder === null) {
		// 	throw new Error('Could not create cuboid index buffer');
		// }
		// const cuboidIndexBuffer = new Uint16Array(VERTEX_INDEX_LIMIT * 36 / 4);

		// for (let j = 0, i = 0; j < VERTEX_INDEX_LIMIT; j += 8, i += 36) {
		// 	// face A
		// 	cuboidIndexBuffer[i]     = j;
		// 	cuboidIndexBuffer[i + 1] = j + 1;
		// 	cuboidIndexBuffer[i + 2] = j + 2;
		// 	cuboidIndexBuffer[i + 3] = j + 3;
		// 	cuboidIndexBuffer[i + 4] = j + 2;
		// 	cuboidIndexBuffer[i + 5] = j + 1;
		// 	// face B
		// 	cuboidIndexBuffer[i + 6] = j + 1;
		// 	cuboidIndexBuffer[i + 7] = j + 6;
		// 	cuboidIndexBuffer[i + 8] = j + 3;
		// 	cuboidIndexBuffer[i + 9] = j + 4;
		// 	cuboidIndexBuffer[i + 10] = j + 3;
		// 	cuboidIndexBuffer[i + 11] = j + 6;
		// 	// face C
		// 	cuboidIndexBuffer[i + 12] = j + 5;
		// 	cuboidIndexBuffer[i + 13] = j + 4;
		// 	cuboidIndexBuffer[i + 14] = j + 7;
		// 	cuboidIndexBuffer[i + 15] = j + 6;
		// 	cuboidIndexBuffer[i + 16] = j + 7;
		// 	cuboidIndexBuffer[i + 17] = j + 4;
		// 	// face D
		// 	cuboidIndexBuffer[i + 18] = j + 7;
		// 	cuboidIndexBuffer[i + 19] = j + 0;
		// 	cuboidIndexBuffer[i + 20] = j + 5;
		// 	cuboidIndexBuffer[i + 21] = j + 2;
		// 	cuboidIndexBuffer[i + 22] = j + 5;
		// 	cuboidIndexBuffer[i + 23] = j + 0;
		// 	// face E
		// 	cuboidIndexBuffer[i + 24] = j + 2;
		// 	cuboidIndexBuffer[i + 25] = j + 3;
		// 	cuboidIndexBuffer[i + 26] = j + 5;
		// 	cuboidIndexBuffer[i + 27] = j + 4;
		// 	cuboidIndexBuffer[i + 28] = j + 5;
		// 	cuboidIndexBuffer[i + 29] = j + 3;
		// 	// face F
		// 	cuboidIndexBuffer[i + 30] = j + 7;
		// 	cuboidIndexBuffer[i + 31] = j + 6;
		// 	cuboidIndexBuffer[i + 32] = j + 0;
		// 	cuboidIndexBuffer[i + 33] = j + 1;
		// 	cuboidIndexBuffer[i + 34] = j + 0;
		// 	cuboidIndexBuffer[i + 35] = j + 6;
		// }

		// // uploading index data
		// gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, cuboidIndexBufferBinder);
		// gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, cuboidIndexBuffer, gl.STATIC_DRAW);
		// this.cuboidIndexBuffer = cuboidIndexBufferBinder;

		this._vpMatrixStack = [];

		// default options
		gl.clearColor(0.0, 0.0, 0.0, 0.0);

		this.onResize((width, height) => {
			this.canvasFrameBuffer.setDimensions(0, 0, width, height);
		});
	}

	reset () {
		this.activeShader = dummyShader;
		this._batchShader = dummyBatchShader;
		this._batchTextures = [];
		this._batchBlending = null;
		this._batchSize = 0; // in bytes
		this._useFrameBuffer(this.canvasFrameBuffer);

		/// #if debug
		drawCount = 0;
		/// #endif
	}

	_useFrameBuffer (frameBuffer: FrameBuffer) {
		if (frameBuffer !== this.activeFrameBuffer) {
			frameBuffer.bindFrameBuffer(this);
			this.activeFrameBuffer = frameBuffer;
		}
		this.gl.viewport(frameBuffer.x, frameBuffer.y, frameBuffer.width, frameBuffer.height);
	}

	useFrameBuffer (frameBuffer: FrameBuffer) {
		this._frameBufferStack.push(frameBuffer);
		this._useFrameBuffer(frameBuffer);
	}

	popFrameBuffer () {
		this._frameBufferStack.pop();
		this._useFrameBuffer(this._frameBufferStack[this._frameBufferStack.length - 1]);
	}

	getCurrentFrameBuffer () {
		return this._frameBufferStack[this._frameBufferStack.length - 1];
	}

	pushVPMatrix (vpMatrix: Array<number>) {
		this._vpMatrixStack.push(vpMatrix);
	}

	popVPMatrix () {
		this._vpMatrixStack.pop();
	}

	getVPMatrix () {
		return this._vpMatrixStack[this._vpMatrixStack.length - 1];
	}

	setupProgram (shader: Shader, textures: Array<Texture>, blending) {
		const gl = this.gl;

		let programChanged = false;
		let shouldUpdateTextures = true;
		if (shader !== this.activeShader) {
			shader.compile(this);

			const program = shader.program;
			gl.useProgram(program.binder);

			// NOTE:
			// Attribute locations are not guaranteed to be contiguous (or start at 0),
			// so enabling/disabling by "attribute count" can break rendering.
			// We just ensure that the program's own attribute locations are enabled.
			const attributes = program.attributes;
			for (const attributeName in attributes) {
				const location = attributes[attributeName];
				if (location >= 0) {
					gl.enableVertexAttribArray(location);
				}
			}

			this.activeShader = shader;
			programChanged = true;
		} else {
			shouldUpdateTextures = shader.program.lastTextures !== textures;
		}

		if (shouldUpdateTextures) {
			const program = shader.program;
			const programTextures = program.textures;
			for (let t = 0; t < textures.length; t++) {
				gl.activeTexture(programTextures[t]);
				textures[t].bindTexture(this.id, gl);
			}

			program.lastTextures = textures;
		}

		// TODO: enbale blending
		// if (blending !== this._activeBlending) {
		// 	const gl = this.gl;
		// 	gl.blendEquation(blending.equation);
		// 	gl.blendFunc(blending.sourceFactor, blending.destinationFactor);
		// 	this._activeBlending = blending;
		// }

		return programChanged;
	}

	prepareBatch (shader: BatchShader, textures: Array<Texture>, blending: any, bytes: number) {
		if (shader !== this._batchShader ||
			textures !== this._batchTextures ||
			blending !== this._batchBlending ||
			(this._batchSize + bytes) > MAX_BYTES_PER_BATCH
		) {
			this.flushBatch();

			this._batchShader = shader;
			this._batchTextures = textures;
			this._batchBlending = blending;
		}

		const objectOffset = this._batchSize;
		this._batchSize += bytes;
		return objectOffset;
	}

	flushBatch () {
		if (this._batchSize === 0) {
			return;
		}

		/// #if debug
		drawCount += 1;
		/// #endif

		this.setupProgram(this._batchShader.shader, this._batchTextures, this._batchBlending);

		const gl = this.gl;
		gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBufferBinder);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.quadIndexBuffer);

		const vpMatrix = this._vpMatrixStack[this._vpMatrixStack.length - 1];
		this._batchShader.render(gl, this.byteView, this._batchSize, vpMatrix);
		// debugger
		this._batchSize = 0;
	}

	/// #if debug
	getDrawCount () {
		return drawCount;
	}
	/// #endif
};

export default Renderer;

// Test implementation for populating index buffers corresponding to a (n, m) grid mesh
// Keeping it around in case we need it later
// _initGridVertexIndexBuffer (n, m) {
// 	const gl = this.gl;
// 	const indexBufferBinder;
// 	if (this._gridIndexBufferBinders[n] === undefined) {
// 		this._gridIndexBufferBinders[n] = [];
// 		indexBufferBinder = gl.createBuffer();
// 	} else {
// 		if (this._gridIndexBufferBinders[n][m] === undefined) {
// 			indexBufferBinder = gl.createBuffer();
// 		} else {
// 			return this._gridIndexBufferBinders[n][m];
// 		}
// 	}
// 	this._gridIndexBufferBinders[n][m] = indexBufferBinder;

// 	const nbVertices = n * m * 6;
// 	if (nbVertices > MAX_VERTEX_INDEX * 6 / 4) {
// 		throw new Error('[SpriteRenderBuffer] Grid is too big, it cannot exceed ' + MAX_VERTEX_INDEX * 6 / 4 + ' vertices')
// 	}

// 	const indexBuffer = new Uint16Array(nbVertices);

// 	let l = 0;
// 	let k = 0;
// 	for (let i = 0; i < n; i += 1) {
// 		for (let j = 0; j < m; j += 1) {
// 			indexBuffer[k]     = l;
// 			indexBuffer[k + 1] = l + m + 1;
// 			indexBuffer[k + 2] = l + m + 2;
// 			indexBuffer[k + 3] = l;
// 			indexBuffer[k + 4] = l + m + 2;
// 			indexBuffer[k + 5] = l + 1;
// 			l += 1;
// 			k += 6;
// 		}
// 		l += 1;
// 	}

// 	// Uploading index data
// 	gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBufferBinder);
// 	gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indexBuffer, gl.STATIC_DRAW);

// 	return indexBufferBinder;
// }
